import { getCookie, setCookie, deleteCookie } from '../../utils/cookieUtils';
import { FEDERATE_LOGIN_PATH } from '../config/authConfig';
import { AppConfig } from './types';

const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
};

const isTokenExpired = (token: string): boolean => {
  try {
    const payload = decodeJWT(token);
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch {
    return true;
  }
};

const clearExpiredToken = (federateIdToken: string): void => {
  deleteCookie(federateIdToken, { path: '/' });
};

const getTokenFromCookie = (federateIdToken: string): string | null => {
  return getCookie(federateIdToken);
};

const setTokenToCookie = (token: string, federateIdToken: string): void => {
  setCookie(federateIdToken, token, {
    path: '/',
    expires: 3600, // 1 hour in seconds
    secure: true,
    sameSite: 'None',
  });
};

// One-time login-attempt values that must survive the round-trip to the Federate
// IdP and back. sessionStorage is per-origin, persists across the cross-origin
// redirect, and is cleared when the tab closes.
const FEDERATE_NONCE_KEY = 'federate_auth_nonce';
const FEDERATE_STATE_KEY = 'federate_auth_state';
const FEDERATE_RETURN_PATH_KEY = 'federate_auth_return_path';

// Cryptographically strong, URL-safe random string used for the nonce and the
// anti-CSRF state value.
const generateRandomToken = (byteLength = 32): string => {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const getRedirectUrl = (config: AppConfig) => {
  const identityProviderAuthEndpoint = `https://${config.identityProvider}${FEDERATE_LOGIN_PATH}`;
  const federateURL = new URL(identityProviderAuthEndpoint);
  federateURL.searchParams.append('client_id', config.federateClientId);
  const redirectUri = `${window.location.origin}/callback`;
  federateURL.searchParams.append('redirect_uri', redirectUri);
  federateURL.searchParams.append('response_type', 'id_token');
  federateURL.searchParams.append('scope', 'openid');

  // Bind the returned id_token to this login attempt: the callback rejects any
  // token whose `nonce` claim does not match the value stored here.
  const nonce = generateRandomToken();
  sessionStorage.setItem(FEDERATE_NONCE_KEY, nonce);
  federateURL.searchParams.append('nonce', nonce);

  // Anti-CSRF: `state` is an unguessable token, not the return path. The real
  // return path is stored separately and only used after `state` matches.
  const state = generateRandomToken();
  sessionStorage.setItem(FEDERATE_STATE_KEY, state);
  const originalPath = `${window.location.pathname}${window.location.search}` || '/';
  sessionStorage.setItem(FEDERATE_RETURN_PATH_KEY, originalPath);
  federateURL.searchParams.append('state', state);

  return federateURL;
};

export interface FederateCallbackResult {
  token: string;
  returnPath: string;
}

/**
 * Validate and consume an OIDC implicit-flow callback from the URL hash.
 * Throws if `state` (anti-CSRF) or the token `nonce` claim (anti-replay) do not
 * match the values stored before the redirect. The stored values are one-time:
 * they are cleared on every call regardless of outcome.
 */
export const consumeFederateCallback = (): FederateCallbackResult => {
  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  const idToken = hashParams.get('id_token');
  const returnedState = hashParams.get('state');

  const expectedState = sessionStorage.getItem(FEDERATE_STATE_KEY);
  const expectedNonce = sessionStorage.getItem(FEDERATE_NONCE_KEY);
  const storedReturnPath = sessionStorage.getItem(FEDERATE_RETURN_PATH_KEY);

  sessionStorage.removeItem(FEDERATE_STATE_KEY);
  sessionStorage.removeItem(FEDERATE_NONCE_KEY);
  sessionStorage.removeItem(FEDERATE_RETURN_PATH_KEY);

  if (!idToken) {
    throw new Error('Federate callback is missing id_token');
  }
  if (!expectedState || returnedState !== expectedState) {
    throw new Error('Federate callback state mismatch (possible login CSRF)');
  }
  const claims = decodeJWT(idToken);
  if (!expectedNonce || claims?.nonce !== expectedNonce) {
    throw new Error('Federate callback nonce mismatch (possible token replay)');
  }

  const returnPath = storedReturnPath && storedReturnPath.startsWith('/') ? storedReturnPath : '/';
  return { token: idToken, returnPath };
};

const login = (config: AppConfig) => {
  const login_url = getRedirectUrl(config);
  window.location.assign(login_url);
};

export const getUserRolesFromFederate = (token: string | null): string[] => {
  if (token) {
    const payload = decodeJWT(token);
    return payload?.role || [];
  } else {
    return [];
  }
};

export const getUserNameFromFederate = (token: string | null): string | null => {
  if (token) {
    const payload = decodeJWT(token);
    return payload?.name || null;
  } else {
    return null;
  }
};

export const getUserLoginFromFederate = (token: string | null): string | null => {
  if (token) {
    const payload = decodeJWT(token);
    return payload?.sub || null;
  } else {
    return null;
  }
};

export const getFederateToken = async (config: AppConfig): Promise<string> => {
  // Tokens enter ONLY through the validated /callback flow (consumeFederateCallback),
  // which stores them in the cookie. Never accept a token straight off the URL hash.
  const cookieToken = getTokenFromCookie(config.federateIdToken);
  if (cookieToken) {
    // Check if token is expired
    if (isTokenExpired(cookieToken)) {
      clearExpiredToken(config.federateIdToken);
      // Redirect to login for new token
      login(config);
      throw Error('Token expired, redirecting to login.');
    }
    setTokenToCookie(cookieToken, config.federateIdToken);
    return cookieToken;
  }

  // No token found, redirect to login
  login(config);
  throw Error('No token available, redirecting to login.');
};
